import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Divider,
} from '@mui/material';
import ConfirmationDialog from '../../../core/components/ui/ConfirmationDialog';
import { useEndServiceMutation, usePauseServiceMutation } from '../hooks/accountSettingsApi';
import { useSelector } from 'react-redux';
import { RootState } from '../../../core/store';
import { openMailBox } from '../../../utils/openMailBox'

type ModalType = 'pause' | 'end' | null;

const ServiceStatus: React.FC = () => {
  const [openModal, setOpenModal] = useState<ModalType>(null);
  const [isLoading, setIsLoading] = useState(false);
  const memberId = useSelector((state: RootState) => state.basicInfo._id) || '';
  const pauseMutation = usePauseServiceMutation();
  const endMutation = useEndServiceMutation();
  

  const handleOpenModal = (type: ModalType) => {
    setOpenModal(type);
  };

  const handleCloseModal = () => {
    setOpenModal(null);
  };

  const handleConfirmAction = async () => {
    try {
      setIsLoading(true);
      
      if (openModal === 'pause') {
        await pauseMutation.mutateAsync(memberId);
      } else if (openModal === 'end') {
        await endMutation.mutateAsync(memberId);
      }
      
      handleCloseModal();
    } catch (error) {
      console.error(`Failed to ${openModal} service:`, error);
    } finally {
      setIsLoading(false);
    }
  };

  const getDialogProps = () => {
    if (openModal === 'pause') {
      return {
        title: 'Confirm Pause Service',
        message: 'Are you sure you want to pause your coaching service? You can resume your sessions at any time.',
        confirmColor: 'primary' as const,
      };
    } else {
      return {
        title: 'Confirm End Service',
        message: 'Are you sure you want to end your coaching service? This action cannot be undone.',
        confirmColor: 'error' as const,
      };
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={600} sx={{ mb: 4 }}>
        Service Management
      </Typography>
      
      <Box sx={{ mb: 5 }}>
        <Typography variant="h6" fontWeight={500} sx={{ mb: 2 }}>
          Pause Service
        </Typography>
        
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          Temporarily pause your coaching sessions. You can resume anytime.
        </Typography>
        
        <Button
          variant="outlined"
          color="primary"
          fullWidth
          size="large"
          onClick={() => openMailBox('support@mindright.io', 'Customer Query for Pause Service ')}
          // onClick={() => handleOpenModal('pause')}
          sx={{ 
            py: 1.5,
            borderRadius: 1,
            textTransform: 'none',
            fontSize: '1rem'
          }}
        >
          Pause Service
        </Button>
      </Box>
      
      <Divider sx={{ my: 4 }} />
      
      <Box>
        <Typography variant="h6" fontWeight={500} sx={{ mb: 2 }}>
          End Service
        </Typography>
        
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          Permanently end your MindRight coaching service.
        </Typography>
        
        <Button
          variant="outlined"
          color="error"
          fullWidth
          size="large"
          onClick={() => openMailBox('support@mindright.io', 'Customer Query for End Service ')}
          //  onClick={() => handleOpenModal('end')}
          sx={{ 
            py: 1.5,
            borderRadius: 1,
            textTransform: 'none',
            fontSize: '1rem',
            borderColor: 'error.main',
            color: 'error.main',
            '&:hover': {
              borderColor: 'error.dark',
              bgcolor: 'rgba(211, 47, 47, 0.04)'
            }
          }}
        >
          End Service
        </Button>
      </Box>

      {openModal && (
        <ConfirmationDialog
          open={openModal !== null}
          onConfirm={handleConfirmAction}
          onCancel={handleCloseModal}
          isLoading={isLoading}
          {...getDialogProps()}
        />
      )}
    </Box>
  );
};

export default ServiceStatus;